// AppShell.jsx
// Adds a fixed footer for navigation. Keeps existing drawer logic intact.
// Footer nav is best used on mobile; on desktop it can be hidden or kept (toggle via showFooterNavOnDesktop).

import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import {
  AppBar,
  Avatar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  Button,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
  DialogContentText,
  Paper,
  BottomNavigation,
  BottomNavigationAction,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link as RouterLink, useLocation, matchPath } from "react-router-dom";

import { useSideNavItems } from "../../pages/app/components/useSideNavItems";

import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Collapse from "@mui/material/Collapse";

const DRAWER_WIDTH_EXPANDED = 280;
const DRAWER_WIDTH_COLLAPSED = 72;
const STORAGE_KEY = "appShell.drawerCollapsed";
const FOOTER_HEIGHT = 64;

function isActivePath(currentPath, itemPath, exact) {
  if (exact) return currentPath === itemPath;
  return (
    currentPath === itemPath ||
    !!matchPath({ path: itemPath, end: false }, currentPath)
  );
}

function NavList({ items, collapsed, onNavigate }) {
  const location = useLocation();

  return (
    <List sx={{ px: collapsed ? 0.5 : 1 }}>
      {items.map((item) => {
        const selected = isActivePath(location.pathname, item.path, item.exact);
        const isDisabled = Boolean(item.disabled);
        const showPanel = Boolean(item.panel) && !collapsed && selected && !isDisabled;

        return (
          <Box key={item.path}>
            <Tooltip
              title={collapsed ? item.label : ""}
              placement="right"
              disableHoverListener={!collapsed}
            >
              <ListItemButton
                component={isDisabled ? "div" : RouterLink}
                to={isDisabled ? undefined : item.path}
                selected={!isDisabled && selected}
                disabled={isDisabled}
                onClick={isDisabled ? undefined : onNavigate}
                sx={{
                  borderRadius: 2,
                  my: 0.5,
                  justifyContent: collapsed ? "center" : "flex-start",
                  px: collapsed ? 1 : 1.5,
                  opacity: isDisabled ? 0.45 : 1,
                  pointerEvents: isDisabled ? "none" : "auto",
                  "&.Mui-selected": {
                    backgroundColor: "rgba(203, 231, 255, 0.12)",
                    fontWeight: 600
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: collapsed ? "auto" : 40,
                    justifyContent: "center",
                    color: isDisabled ? "text.disabled" : "inherit",
                  }}
                >
                  {item.icon}
                </ListItemIcon>

                {!collapsed && (
                  <ListItemText
                    primary={item.label}
                    secondary={item.secondary}
                    primaryTypographyProps={{ noWrap: true }}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                )}
              </ListItemButton>
            </Tooltip>

            {/* Optional inline panel */}
            {item?.panel && (
              <Collapse in={showPanel} timeout="auto" unmountOnExit>
                <Box
                  sx={{
                    ml: 5,          // lines up under text (icon column)
                    mr: 1,
                    mb: 1,
                    mt: 0.5,
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: "action.hover",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {typeof item.panel === "function" ? item.panel() : item.panel}
                </Box>
              </Collapse>
            )}
          </Box>
        );
      })}
    </List>
  );
}

NavList.propTypes = {
  items: PropTypes.array.isRequired,
  collapsed: PropTypes.bool.isRequired,
  onNavigate: PropTypes.func,
};

function FooterNav({ items, hidden }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Choose the active item based on current path (supports nested routes)
  const activePath =
    items.find((i) => isActivePath(location.pathname, i.path, i.exact))?.path ||
    items[0]?.path ||
    "";

  if (hidden) return null;

  return (
    <Paper
      elevation={6}
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (t) => t.zIndex.appBar,
        borderTop: "1px solid",
        borderColor: "divider",
      }}
      className="no-print"
    >
      <BottomNavigation
        showLabels
        value={activePath}
        onChange={(_, newValue) => {
          if (!newValue) return;

          if (newValue === "/app/new") {
            const ok = window.confirm('Are you sure you want to create a new FPZ report? This will clear the current FPZ data.');
            if (!ok) return;
            dispatch({ type: 'CLEAR_FPZ' });
          }
          
          navigate(newValue);
        }}
        sx={{ height: FOOTER_HEIGHT }}
      >
        {items.map((item) => (
          <BottomNavigationAction
            key={item.path}
            label={item.label}
            value={item.path}
            icon={item.icon}
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
}

FooterNav.propTypes = {
  items: PropTypes.array.isRequired,
  hidden: PropTypes.bool,
};

export default function AppShell({
  children,
  appName = "Abra Controls Directive 83",
  heroImageUrl,
  navItems = [],
  footerNavItems, // optional override; defaults to navItems
  showFooterNavOnDesktop = false,
  user = { name: "Sign Issn", email: "" },
  isAuthed = false,
  onLogout,
}) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const sideNavItems = useSideNavItems();

  const { selectedTreatmentWells } = useSelector(state => state.fpzReducer);

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = React.useState(null);

  const [open, setOpen] = React.useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogContent, setDialogContent] = useState("");
  const [dialogContentText, setDialogContentText] = useState("");
  const [dialogActions, setDialogActions] = useState(null);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const [collapsed, setCollapsed] = React.useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : false;
    } catch {
      return false;
    }
  });

  React.useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(collapsed));
    } catch {
      // ignore
    }
  }, [collapsed]);

  const drawerWidth = collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;

  const toggleMobileDrawer = () => setMobileOpen((v) => !v);
  const toggleDesktopCollapse = () => setCollapsed((v) => !v);

  const openUserMenu = (e) => setUserMenuAnchor(e.currentTarget);
  const closeUserMenu = () => setUserMenuAnchor(null);

  const handleLogout = () => {
    closeUserMenu();
    dispatch({type: 'LOGOUT' });
  };

  const drawerHeader = (
    <Toolbar
      sx={{
        px: collapsed ? 1 : 2,
        display: "flex",
        alignItems: "center",
        justifyContent: collapsed ? "center" : "space-between",
        gap: 1,
      }}
    >
      {!collapsed ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: "primary.main",
              flex: "0 0 auto",
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {appName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              App
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            bgcolor: "primary.main",
          }}
        />
      )}

      {isDesktop && (
        <Tooltip title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
          <IconButton onClick={toggleDesktopCollapse} size="small">
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );

  const drawerFooter = (
    <Box
      sx={{
        p: collapsed ? 1 : 2,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        justifyContent: collapsed ? "center" : "flex-start",
      }}
    >
      <Tooltip
        title={collapsed ? (user?.name || "User") : ""}
        placement="right"
        disableHoverListener={!collapsed}
      >
        <Avatar sx={{ width: 36, height: 36 }}>
          {(user?.name || "U").slice(0, 1).toUpperCase()}
        </Avatar>
      </Tooltip>

      {!collapsed && (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {user?.name || "User"}
          </Typography>
          <Typography variant="caption" noWrap>
            {user?.email || ""}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundImage: heroImageUrl
          ? `url(${heroImageUrl})`
          : `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        filter: heroImageUrl ? "brightness(0.55)" : "none",
        color: "common.white",
      }}
    >
      {drawerHeader}
      <Divider />
      <Box sx={{ py: 1, flex: 1, overflowY: "auto" }}>
        <NavList
          items={sideNavItems}
          collapsed={collapsed}
          onNavigate={!isDesktop ? () => setMobileOpen(false) : undefined}
        />
      </Box>
      <Divider />
      {drawerFooter}
    </Box>
  );

  const footerItems = footerNavItems || navItems;
  const hideFooter = isDesktop && !showFooterNavOnDesktop;

  return (
    <Box sx={{ display: "flex", minHeight: "100dvh" }}>
      <CssBaseline />
      {/* Print Styles */}
      <style>
        {`
          @media print {
            body { background: white; counter-reset: page; }
            .no-print { display: none !important; }
            .print-only { display: block !important; }
            .print-text { font-size: .6em !important; }

            .print-footer {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              text-align: center;
              font-size: 10pt;
              color: #666;
            }

            .print-footer::after {
              counter-increment: page;
              /*content: "Page " counter(page);*/
            }

            .footer { page-break-after: always; }
            .content { margin-bottom: 25mm; }
          }

          @media screen {
            .print-only { display: none; }
          }
        `}
      </style>
      
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: "background.paper",
          color: "text.primary",
        }}
        className="no-print"
      >
        <Toolbar sx={{ gap: 1 }}>
          {!isDesktop ? (
            <IconButton edge="start" onClick={toggleMobileDrawer} size="large">
              <MenuIcon />
            </IconButton>
          ) : (
            <IconButton onClick={toggleDesktopCollapse} size="large">
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          )}

          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {appName}
          </Typography>

          <Box sx={{ flex: 1 }} />

          <Tooltip title={user?.name || "Account"}>
            <IconButton onClick={openUserMenu} size="large">
              <Avatar sx={{ width: 32, height: 32 }}>
                {(user?.name || "U").slice(0, 1).toUpperCase()}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={closeUserMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem disabled>
              <Box sx={{ minWidth: 220 }}>
                <Typography variant="body2" fontWeight={700} noWrap>
                  {user?.name || "User"}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {user?.email || ""}
                </Typography>
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem component={RouterLink} to="/app/account/profile" onClick={closeUserMenu}>
              Settings
            </MenuItem>
            <MenuItem onClick={handleLogout}>Log out</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Desktop drawer */}
      {isDesktop && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            whiteSpace: "nowrap",
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              boxSizing: "border-box",
              overflowX: "hidden",
              transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.shortest,
              }),
              borderRight: `1px solid ${theme.palette.divider}`,
              bgcolor: "background.default",
            },
          }}
          className="no-print"
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Mobile drawer */}
      {!isDesktop && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={toggleMobileDrawer}
          ModalProps={{ keepMounted: true }}
          sx={{
            [`& .MuiDrawer-paper`]: {
              width: DRAWER_WIDTH_EXPANDED,
              boxSizing: "border-box",
            },
          }}
        >
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Toolbar sx={{ px: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "primary.main" }} />
                <Typography variant="subtitle1" fontWeight={700} noWrap>
                  {appName}
                </Typography>
              </Box>
            </Toolbar>
            <Divider />
            <Box sx={{ py: 1, flex: 1, overflowY: "auto" }}>
              <NavList items={sideNavItems} collapsed={false} onNavigate={() => setMobileOpen(false)} />
            </Box>
            <Divider />
            <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar sx={{ width: 36, height: 36 }}>
                {(user?.name || "U").slice(0, 1).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {user?.name || "User"}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {user?.email || ""}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Drawer>
      )}

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default",
          minWidth: 0,
        }}
      >
        <Toolbar />

        <Box
          sx={{
            flex: 1,
            p: { xs: 2, md: 3 },
            maxWidth: 1400,
            width: "100%",
            mx: "auto",
            // keep content from being hidden behind footer nav
            pb: { xs: `${FOOTER_HEIGHT + 16}px`, md: hideFooter ? 24 : `${FOOTER_HEIGHT + 16}px` },
          }}
        >
          {children}
        </Box>
      </Box>

      {/* Footer navigation */}
      <FooterNav items={footerItems} hidden={hideFooter} />
    </Box>
  );
}

AppShell.propTypes = {
  children: PropTypes.node,
  appName: PropTypes.string,
  heroImageUrl: PropTypes.string,
  navItems: PropTypes.array,
  footerNavItems: PropTypes.array,
  showFooterNavOnDesktop: PropTypes.bool,
  user: PropTypes.shape({ name: PropTypes.string, email: PropTypes.string }),
  onLogout: PropTypes.func,
};
